import { Box, Button, Group, Modal, NumberInput, Switch, TextInput } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import HelpPopover from '../HelpPopover/HelpPopover';
import SettingRow from '../SettingRow/SettingRow';
import { HELP, applyExample } from './aiModelsHelp';

const LlmModelModal = ({ model: passedModel, provider, onClose, onSave }) => {
  const fetch = useFetch();
  const [model, setModel] = useState(passedModel);
  const [saveError, setSaveError] = useState();

  useEffect(() => {
    setModel(passedModel);
    setSaveError(undefined);
  }, [passedModel]);

  if (model === undefined || !provider) {
    return '';
  }

  const set = (field) => (value) => setModel((current) => ({ ...current, [field]: value }));
  const modelId = model.modelId || '';
  const label = model.label || '';
  // Seeded providers run their legacy branches, which never send reasoning back.
  const offersEchoReasoning = provider.apiStyle === 'openai' && !provider.seeded;
  const tokenInput = (field) => (
    <NumberInput
      id={field}
      size="xs"
      w={160}
      required
      min={1}
      allowDecimal={false}
      allowNegative={false}
      thousandSeparator=","
      value={model[field] ?? ''}
      onChange={(value) => set(field)(value === '' ? undefined : value)}
    />
  );

  const save = (event) => {
    event.preventDefault();
    setSaveError(undefined);

    const payload = {
      llmModel: {
        modelId: modelId.trim(),
        label: label.trim(),
        contextWindow: model.contextWindow,
        maxOutputTokens: model.maxOutputTokens,
        echoReasoning: offersEchoReasoning && !!model.echoReasoning,
        enabled: model.enabled ?? true,
        ...(!model.id && { providerId: provider.id }),
      },
    };
    const request = model.id
      ? fetch(`/api/admin/llmModels/${model.id}`, { method: 'PUT', body: JSON.stringify(payload) })
      : fetch('/api/admin/llmModels', { method: 'POST', body: JSON.stringify(payload) });

    request
      .then(() => {
        onSave();
        onClose();
      })
      .catch(setSaveError);
  };

  return (
    <Modal
      opened={!!passedModel}
      onClose={onClose}
      size="lg"
      title={`${model.id ? 'Edit' : 'Add'} model on ${provider.label}`}
    >
      <Box component="form" ta="left" onSubmit={save}>
        <SettingRow
          label="Model id*"
          htmlFor="modelId"
          help={<HelpPopover {...HELP.modelId} onUse={applyExample(set('modelId'), modelId)} />}
        >
          <TextInput id="modelId" size="xs" required value={modelId} onChange={(e) => set('modelId')(e.target.value)} />
        </SettingRow>
        <SettingRow label="Label*" htmlFor="label">
          <TextInput id="label" size="xs" required value={label} onChange={(e) => set('label')(e.target.value)} />
        </SettingRow>
        <SettingRow
          label="Context window*"
          htmlFor="contextWindow"
          help={<HelpPopover {...HELP.contextWindow} onUse={applyExample(set('contextWindow'), model.contextWindow)} />}
        >
          {tokenInput('contextWindow')}
        </SettingRow>
        <SettingRow
          label="Max output tokens*"
          htmlFor="maxOutputTokens"
          help={
            <HelpPopover
              {...HELP.maxOutputTokens}
              onUse={applyExample(set('maxOutputTokens'), model.maxOutputTokens)}
            />
          }
        >
          {tokenInput('maxOutputTokens')}
        </SettingRow>
        {offersEchoReasoning && (
          <SettingRow
            label="Send reasoning back on tool turns"
            htmlFor="echoReasoning"
            help={<HelpPopover {...HELP.echoReasoning} />}
          >
            <Switch
              id="echoReasoning"
              size="xs"
              checked={!!model.echoReasoning}
              onChange={(e) => set('echoReasoning')(e.currentTarget.checked)}
            />
          </SettingRow>
        )}
        <SettingRow label="Enabled" htmlFor="enabled">
          <Switch
            id="enabled"
            size="xs"
            checked={model.enabled ?? true}
            onChange={(e) => set('enabled')(e.currentTarget.checked)}
          />
        </SettingRow>
        {saveError && <ErrorMessage message={saveError.message} />}
        <Group justify="flex-end" gap="xs" py="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={modelId.trim() === '' || label.trim() === '' || !model.contextWindow || !model.maxOutputTokens}
          >
            <IconDeviceFloppy size={14} /> Save
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};

export default LlmModelModal;
